/**
 * Chamadas do iframe. Tudo passa pelo proxy REST do host
 * (`/api/apps/qualidade/<caminho>` → `rest_base_url` + `/<caminho>`), exceto o
 * upload de anexos, que vai direto à rota de ficheiros do host (multipart; o
 * proxy REST só passa texto).
 */

import type { AppClient } from "@koortex/apps-sdk"
import type {
  ClientImportReport,
  ClientImportRow,
  Complaint,
  ComplaintAction,
  ComplaintDetail,
  ComplaintFile,
  ComplaintLogEntry,
  Dashboard,
  NoteField,
  Settings,
  Stage,
  SurveysOverview,
} from "../../shared/domain"

interface Envelope<T> {
  success: boolean
  data?: T
  error?: string
}

function unwrap<T>(env: Envelope<T>): T {
  if (!env?.success) throw new Error(env?.error ?? "Pedido falhou.")
  return env.data as T
}

/** Mensagem legível de um erro do SDK (o corpo do host traz `error`). */
export function apiErrorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string } })?.body
  if (body?.error) return body.error
  return err instanceof Error ? err.message : String(err)
}

export interface UserOption {
  id: string
  name: string
  email: string | null
}

export interface ErpClient {
  id: string
  provider: string
  external_id: string
  external_client_number: string | null
  name: string
  tax_id: string | null
  email: string | null
  phone: string | null
  entity_id: string | null
  is_active: boolean
  metadata?: Record<string, unknown> | null
}

export interface CampaignInput {
  name: string
  starts_at: string | null
  ends_at: string | null
  reminder_after_days: number | null
  reminder_max: number
  search: string
}

export interface ComplaintFilters {
  status?: string
  stage?: string
  category?: string
  severity?: string
  owner_user_id?: string
  entity_id?: string
  q?: string
}

export type ComplaintEdit = Partial<
  Record<
    "category" | "severity" | "owner_user_id" | "due_at" | "expectation" | "entity_id" | "ext_client_id" | "estab" | "invoice_ref" | "invoice_date",
    string | null
  >
> & { notes?: Partial<Record<NoteField, string>> }

export function makeApi(client: AppClient) {
  const bo = (path: string) => `backoffice/${path}`
  const get = <T>(path: string) => client.get<Envelope<T>>(bo(path)).then(unwrap)
  const post = <T>(path: string, body: unknown) => client.post<Envelope<T>>(bo(path), body).then(unwrap)
  const patch = <T>(path: string, body: unknown) => client.patch<Envelope<T>>(bo(path), body).then(unwrap)

  return {
    dashboard: (year?: number) => get<Dashboard>(`dashboard${year ? `?year=${year}` : ""}`),

    users: () => get<{ users: UserOption[]; me: string | null }>("users"),

    settings: () => get<Settings>("settings"),
    saveSettings: (s: Settings) => post<Settings>("settings", s),

    complaints: (f: ComplaintFilters = {}) => {
      const qs = new URLSearchParams()
      for (const [k, v] of Object.entries(f)) if (v) qs.set(k, v)
      const s = qs.toString()
      return get<{ items: Complaint[]; truncated: boolean }>(`complaints${s ? `?${s}` : ""}`)
    },
    complaint: (id: string) => get<ComplaintDetail>(`complaints/${id}`),
    createComplaint: (body: Record<string, unknown>) => post<Complaint>("complaints", body),
    updateComplaint: (id: string, body: ComplaintEdit) => patch<Complaint>(`complaints/${id}`, body),
    changeStage: (
      id: string,
      stage: Stage,
      message?: string,
      resolution?: { resolution_type: string; resolution_text: string },
    ) => post<Complaint>(`complaints/${id}/stage`, { stage, message, ...(resolution ?? {}) }),
    cancelComplaint: (id: string, reason: string) => post<Complaint>(`complaints/${id}/cancel`, { reason }),
    addNote: (id: string, message: string, visibility: "internal" | "public") =>
      post<ComplaintLogEntry>(`complaints/${id}/notes`, { message, visibility }),
    addAction: (id: string, body: Partial<ComplaintAction>) => post<ComplaintAction>(`complaints/${id}/actions`, body),
    updateAction: (id: string, actionId: string, body: Partial<ComplaintAction>) =>
      patch<ComplaintAction>(`complaints/${id}/actions/${actionId}`, body),

    /** Envia o ficheiro ao host e regista-o na reclamação. */
    attachFile: async (id: string, file: File, visibility: "internal" | "public" = "internal") => {
      const fd = new FormData()
      fd.append("file", file)
      const up = await client
        .post<Envelope<{ key: string; size: number; content_type: string | null }>>("files", undefined, { body: fd })
        .then(unwrap)
      return post<ComplaintFile>(`complaints/${id}/files`, {
        key: up.key,
        file_name: file.name,
        content_type: up.content_type ?? file.type,
        size: up.size ?? file.size,
        visibility,
      })
    },
    fileUrl: (id: string, fileId: string) => get<{ url: string; file_name: string | null }>(`complaints/${id}/files/${fileId}`),

    surveys: () => get<SurveysOverview>("surveys"),
    createCampaign: (body: CampaignInput) => post<{ id: string }>("campaigns", body),
    updateCampaign: (id: string, body: Partial<CampaignInput> & { status?: string }) => patch<{ id: string }>(`campaigns/${id}`, body),
    sendCampaign: (id: string, limit = 50) =>
      post<{ sent: number; failed: number; already: number; remaining: number; skipped?: string }>(`campaigns/${id}/send`, { limit }),

    clients: (search?: string, cursor?: string) => {
      const qs = new URLSearchParams()
      if (search) qs.set("search", search)
      if (cursor) qs.set("cursor", cursor)
      return get<{ items: ErpClient[]; next_cursor: string | null }>(`clients?${qs}`)
    },

    importClients: (rows: ClientImportRow[], dryRun: boolean) =>
      post<ClientImportReport>("clients/import", { rows, dry_run: dryRun }),
    lookupClient: (q: { number?: string; estab?: string; entity_id?: string }) => {
      const qs = new URLSearchParams()
      for (const [k, v] of Object.entries(q)) if (v) qs.set(k, v)
      return get<{ items: ErpClient[] }>(`clients/lookup?${qs}`)
    },
  }
}

export type Api = ReturnType<typeof makeApi>
